<template>
  <div>
    <v-form ref="formCondition" autocomplete="off" v-model="valid">
      <fieldset class="zone-block pa-3 my-2 mw-900">
        <div class="pre-title my-1">
          <span>{{ $t("label.condition") }}</span>
        </div>
        <!-- DATE REPORT -->
        <v-row class="mx-1 mw-1000">
          <v-col cols="1"></v-col>
          <v-col cols="4" class="pb-0">
            <span>{{ $t("label.referenceDate") }}</span>
            <div class="mt-2">
              <date-picker
                :label="$t('label.startDate')"
                hide-details
                required
                :rules="[(v) => required(searchCondition.referenceStartDate)]"
                v-model="searchCondition.referenceStartDate"
                :max="searchCondition.referenceEndDate"
                clearable
              />
            </div>
            <div class="mt-3">
              <date-picker
                dense
                required
                hide-details
                :label="$t('label.endDate')"
                :rules="[(v) => required(searchCondition.referenceEndDate)]"
                v-model="searchCondition.referenceEndDate"
                :min="searchCondition.referenceStartDate"
                clearable
              />
            </div>
          </v-col>
          <v-col cols="2"></v-col>
          <v-col cols="4" class="pb-0">
            <span>{{ $t("label.compareDate") }}</span>
            <div class="mt-2">
              <date-picker
                :label="$t('label.startDate')"
                hide-details
                readonly
                required
                :rules="[(v) => required(searchCondition.compareStartDate)]"
                v-model="searchCondition.compareStartDate"
                clearable
              />
            </div>
            <div class="mt-3">
              <date-picker
                :label="$t('label.endDate')"
                hide-details
                readonly
                required
                :rules="[(v) => required(searchCondition.compareEndDate)]"
                v-model="searchCondition.compareEndDate"
                clearable
              />
            </div>
          </v-col>
        </v-row>

        <!-- STORE-CATEGORY -->
        <v-row class="mx-1 mw-1000">
          <v-col cols="1"></v-col>
          <v-col cols="4" class="pb-0">
            <span>{{ $t("label.targetStore") }}</span>
            <div class="mt-2">
              <custom-autocomplete-single
                required
                :label="$t('label.storeCode')"
                :items="stores"
                hide-details
                v-model.trim="searchCondition.storeCode"
                :itemValue="'value'"
                :itemText="'text'"
                :no-data-text="$t('msg.noData')"
                :rules="[(v) => required(searchCondition.storeCode)]"
              />
            </div>
          </v-col>
          <v-col cols="2"></v-col>
          <v-col cols="4" class="pb-0">
            <span>{{ $t("label.targetClassification") }}</span>
            <div class="mt-2">
              <custom-autocomplete-multi
                required
                :label="$t('label.majorCategory')"
                :items="majorCategories"
                v-model="searchCondition.majorCodes"
                hide-details
                :rules="[(v) => requiredArr(searchCondition.majorCodes)]"
              />
            </div>
            <div class="mt-3">
              <custom-autocomplete-multi
                required
                :label="$t('label.middleCategory')"
                :items="middleCategories"
                v-model="searchCondition.middleCodes"
                hide-details
                :rules="[(v) => requiredArr(searchCondition.middleCodes)]"
              />
            </div>
            <div class="mt-3">
              <custom-autocomplete-multi
                required
                :label="$t('label.displayItem')"
                :items="displaySourceItems"
                v-model="searchCondition.displayItems"
                hide-details
                :rules="[(v) => requiredArr(searchCondition.displayItems)]"
              />
            </div>
          </v-col>
        </v-row>
        <v-row class="mx-1 mw-1000">
          <v-col cols="7"></v-col>
          <v-col cols="4" class="text-right">
            <button-icon
              class="primary btn-primary"
              :icon="$t('ic.result')"
              :text="$t('label.apply')"
              @click="updateDataChart"
            />
          </v-col>
        </v-row>
      </fieldset>
    </v-form>
  </div>
</template>

<script>
import { COMPONENT_NAME } from "@/common/report.constant.js";
import DatePicker from "@/components/common/DatePicker.vue";
import MultiSelectField from "@/components/common/MultiSelectField";
import ButtonIcon from "@/components/common/ButtonIcon";
import { VExpansionPanel, VExpansionPanelContent } from "vuetify";
import CustomAutocompleteSingle from "@/components/common/CustomAutocompleteSingle.vue";
import CustomAutocompleteMulti from "@/components/common/CustomAutocompleteMulti.vue";
import {
  GET_ALL_STORES,
  GET_ALL_MAJOR_CATEGORIES,
  GET_ALL_MIDDLE_CATEGORIES,
} from "@/api/report";
import { getYearBefore } from "@/common/constant";
import { required, requiredArr } from "@/common/common-function.js";

export default {
  name: COMPONENT_NAME.REPORT_CONDITION,
  components: {
    DatePicker,
    MultiSelectField,
    ButtonIcon,
    VExpansionPanel,
    VExpansionPanelContent,
    CustomAutocompleteSingle,
    CustomAutocompleteMulti,
  },
  props: {
    searchCondition: {
      type: Object,
      require: true,
    },
    displaySourceItems: {
      type: Array,
      required: true,
    },
  },
  watch: {
    "searchCondition.referenceStartDate": {
      handler(value) {
        if (!value) {
          return;
        }
        this.searchCondition.compareStartDate = getYearBefore(value);
      },
    },
    "searchCondition.referenceEndDate": {
      handler(value) {
        if (!value) {
          return;
        }
        this.searchCondition.compareEndDate = getYearBefore(value);
      },
    },
  },
  data() {
    return {
      COMPONENT_NAME: COMPONENT_NAME,
      valid: true,
      stores: [],
      majorCategories: [],
      middleCategories: [],
      panels: [0],
      getYearBefore,
      required,
      requiredArr,
    };
  },
  methods: {
    updateDataChart() {
      this.$refs.formCondition.validate();
      if (this.valid) {
        this.$emit("updateDataChart");
      }
    },
    async fetchStores() {
      try {
        let { data } = await GET_ALL_STORES();
        if (data) {
          data.forEach((element) => {
            this.stores.push({
              text: element.storeCode + "_" + element.storeName,
              value: element.storeCode,
            });
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    async fetchMajorCategories() {
      try {
        let { data } = await GET_ALL_MAJOR_CATEGORIES();
        if (data) {
          data.forEach((element) => {
            this.majorCategories.push({
              text: element.majorCode + "_" + element.majorNameKanji,
              value: element.majorCode,
            });
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    async fetchMiddleCategories() {
      try {
        let { data } = await GET_ALL_MIDDLE_CATEGORIES();
        if (data) {
          data.forEach((element) => {
            this.middleCategories.push({
              text: element.middleCode + "_" + element.middleNameKanji,
              value: element.middleCode,
              majorCode: element.majorCode,
            });
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
  },
  created() {
    this.fetchStores();
    this.fetchMajorCategories();
    this.fetchMiddleCategories();
  },
};
</script>

<style lang="scss" scoped></style>
